import { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { styles } from "../styles";
import { createCustomer } from "../models/models";
import { hasCapability, isAdminTier } from "../utils/capabilities";
import { isTaxCertExpired } from "../utils/billTotals";
import HighlightText from "../components/HighlightText";
import ContactsEditor from "../components/ContactsEditor";
import BackButton from "../components/BackButton";

// Fields the inline Add/Edit form covers. Kept here as a list so the
// form, the save payload, and the info-card readout stay in lockstep
// without us re-typing field names three places.
const EMPTY_FORM = {
  name: "",
  phone: "",
  email: "",
  street: "",
  suite: "",
  city: "",
  state: "",
  zip: "",
  billingStreet: "",
  billingCity: "",
  billingState: "",
  billingZip: "",
  billingContactName: "",
  billingContactPhone: "",
  billingContactEmail: "",
  notes: "",
  // Tax-exempt block. taxExempt drives the toggle; the other fields only
  // matter (and only get rendered) when the toggle is on.
  taxExempt: false,
  taxExemptCert: "",
  taxExemptCertExpires: "",
  taxExemptReason: "",
};

const buildFormFrom = (customer) => {
  if (!customer) return { ...EMPTY_FORM };
  const out = { ...EMPTY_FORM };
  for (const key of Object.keys(EMPTY_FORM)) {
    if (customer[key] !== undefined && customer[key] !== null) {
      out[key] = customer[key];
    }
  }
  return out;
};

// Inline form used both for adding a new customer and editing an
// existing one. Two-column grid so it fits on one screen without
// scrolling, collapses to single column on narrow widths.
function CustomerForm({
  value,
  onChange,
  onSave,
  onCancel,
  onDelete,
  canDelete,
  busy,
  mode,
  customerId,
  contacts,
  actions,
  currentUserEmail,
}) {
  // Type-DELETE confirmation gate. Used to clean up imported QB test
  // customers without leaving the page; same pattern as the turnover
  // wipe in ManageUsersPage (lower stakes here since it's one customer,
  // but accidental deletes still hurt).
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const deleteConfirmMatches =
    deleteConfirmText.trim().toUpperCase() === "DELETE";
  const f = (key, label, opts = {}) => (
    <div>
      <label
        style={{ display: "block", fontSize: "12px", color: "#666" }}
      >
        {label}
      </label>
      <input
        style={{ ...styles.input, margin: 0, width: "100%" }}
        type={opts.type || "text"}
        value={value[key] || ""}
        onChange={(e) => onChange({ ...value, [key]: e.target.value })}
        placeholder={opts.placeholder || ""}
      />
    </div>
  );
  return (
    <div style={{ ...styles.card, maxWidth: "780px" }}>
      <h3 style={{ marginTop: 0 }}>
        {mode === "edit" ? "Edit Customer" : "Add Customer"}
      </h3>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "10px",
        }}
      >
        {f("name", "Customer Name", { placeholder: "Required" })}
        {f("phone", "Main Phone", { type: "tel" })}
        {f("email", "Main Email", { type: "email" })}
      </div>

      <h4 style={{ margin: "16px 0 6px" }}>Address</h4>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "10px",
        }}
      >
        {f("street", "Street")}
        {f("suite", "Suite")}
        {f("city", "City")}
        {f("state", "State")}
        {f("zip", "Zip")}
      </div>

      <h4 style={{ margin: "16px 0 6px" }}>Billing</h4>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "10px",
        }}
      >
        {f("billingStreet", "Billing Street")}
        {f("billingCity", "Billing City")}
        {f("billingState", "Billing State")}
        {f("billingZip", "Billing Zip")}
        {f("billingContactName", "Billing Contact")}
        {f("billingContactPhone", "Billing Phone", { type: "tel" })}
        {f("billingContactEmail", "Billing Email", { type: "email" })}
      </div>

      <h4 style={{ margin: "16px 0 6px" }}>Tax Status</h4>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontSize: "13px",
        }}
      >
        <input
          type="checkbox"
          checked={!!value.taxExempt}
          onChange={(e) =>
            onChange({ ...value, taxExempt: e.target.checked })
          }
        />
        <span>
          Tax exempt
          <span style={{ color: "#666", fontSize: "12px", marginLeft: "4px" }}>
            (no sales tax will be applied to this customer's quotes / invoices)
          </span>
        </span>
      </label>
      {value.taxExempt && (
        <div
          style={{
            marginTop: "8px",
            padding: "10px",
            background: "#fff8e0",
            border: "1px solid #f0d27a",
            borderRadius: "4px",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "10px",
          }}
        >
          <div>
            <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
              Exemption certificate #
            </label>
            <input
              style={{ ...styles.input, margin: 0, width: "100%" }}
              placeholder="e.g. TX-12345678"
              value={value.taxExemptCert || ""}
              onChange={(e) =>
                onChange({ ...value, taxExemptCert: e.target.value })
              }
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
              Reason
            </label>
            <input
              style={{ ...styles.input, margin: 0, width: "100%" }}
              placeholder="Govt entity / Resale / Nonprofit..."
              value={value.taxExemptReason || ""}
              onChange={(e) =>
                onChange({ ...value, taxExemptReason: e.target.value })
              }
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
              Cert expires (optional)
            </label>
            <input
              type="date"
              style={{ ...styles.input, margin: 0, width: "100%" }}
              value={value.taxExemptCertExpires || ""}
              onChange={(e) =>
                onChange({
                  ...value,
                  taxExemptCertExpires: e.target.value,
                })
              }
            />
          </div>
        </div>
      )}

      <h4 style={{ margin: "16px 0 6px" }}>Notes</h4>
      <textarea
        style={{ ...styles.textarea, maxWidth: "100%" }}
        rows={3}
        value={value.notes || ""}
        onChange={(e) => onChange({ ...value, notes: e.target.value })}
      />

      {mode === "edit" && customerId && actions && (
        <ContactsEditor
          customerId={customerId}
          contacts={contacts || []}
          actions={actions}
          currentUserEmail={currentUserEmail}
        />
      )}

      <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
        <button
          style={{ ...styles.primaryButton, margin: 0 }}
          onClick={onSave}
          disabled={busy || !value.name.trim()}
        >
          {busy ? "Saving..." : "Save"}
        </button>
        <button
          style={{ ...styles.button, margin: 0 }}
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
        {mode === "edit" && canDelete && (
          <button
            style={{
              ...styles.button,
              margin: 0,
              marginLeft: "auto",
              background: "#c44",
              color: "white",
            }}
            onClick={() => {
              setShowDeleteConfirm(true);
              setDeleteConfirmText("");
            }}
            disabled={busy}
          >
            Delete Customer
          </button>
        )}
      </div>

      {showDeleteConfirm && (
        <div
          style={{
            marginTop: "12px",
            padding: "10px",
            background: "#fff5f5",
            border: "2px solid #c44",
            borderRadius: "6px",
          }}
        >
          <div style={{ fontSize: "13px", color: "#7a1f1f" }}>
            <strong>Delete <em>{value.name}</em> permanently?</strong>{" "}
            Buildings, equipment, and any tickets/jobs/invoices tied to
            this customer will stay in Firestore but become unreachable
            from the customer list. Cannot be undone from the UI.
          </div>
          <div style={{ marginTop: "8px" }}>
            <label style={{ fontSize: "12px", display: "block" }}>
              Type <code>DELETE</code> to confirm:
            </label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              style={{
                ...styles.input,
                margin: "4px 0 0",
                width: "100%",
                maxWidth: "240px",
              }}
              disabled={busy}
              autoFocus
            />
          </div>
          <div style={{ marginTop: "10px", display: "flex", gap: "8px" }}>
            <button
              style={{
                ...styles.button,
                margin: 0,
                background: "#c44",
                color: "white",
                opacity: deleteConfirmMatches && !busy ? 1 : 0.5,
              }}
              disabled={!deleteConfirmMatches || busy}
              onClick={() => {
                onDelete && onDelete();
                setShowDeleteConfirm(false);
                setDeleteConfirmText("");
              }}
            >
              {busy ? "Deleting..." : "Confirm delete"}
            </button>
            <button
              style={{ ...styles.button, margin: 0 }}
              disabled={busy}
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeleteConfirmText("");
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Read-only info readout for an expanded customer row.
function CustomerInfoCard({ customer, contacts = [], onEdit }) {
  const addressLine = [
    [customer.street, customer.suite].filter(Boolean).join(", Ste "),
    [customer.city, customer.state].filter(Boolean).join(", "),
    customer.zip,
  ]
    .filter(Boolean)
    .join(" · ");
  const billingAddressLine = [
    customer.billingStreet,
    [customer.billingCity, customer.billingState].filter(Boolean).join(", "),
    customer.billingZip,
  ]
    .filter(Boolean)
    .join(" · ");

  // Group contacts under their department. Anything without a department
  // falls under "Other".
  const byDept = new Map();
  for (const c of contacts) {
    const key = (c.department || "Other").trim() || "Other";
    if (!byDept.has(key)) byDept.set(key, []);
    byDept.get(key).push(c);
  }
  const deptOrder = [...byDept.keys()].sort();

  const dt = { fontSize: "12px", color: "#666", margin: "0 0 2px" };
  const dd = { fontSize: "13px", margin: "0 0 8px" };

  return (
    <div
      style={{
        ...styles.card,
        background: "#fdfdfd",
        border: "1px solid #eee",
        marginTop: "6px",
      }}
    >
      {customer.taxExempt && (() => {
        const certExpired = isTaxCertExpired(customer);
        return (
          <div
            style={{
              marginBottom: "10px",
              padding: "6px 10px",
              background: certExpired ? "#fdecec" : "#fff8e0",
              border: certExpired ? "1px solid #e0a0a0" : "1px solid #f0d27a",
              borderRadius: "4px",
              fontSize: "12px",
              color: certExpired ? "#8a1f1f" : "#6a4900",
            }}
          >
            <strong>TAX EXEMPT</strong>
            {customer.taxExemptCert ? ` · Cert ${customer.taxExemptCert}` : ""}
            {customer.taxExemptReason ? ` · ${customer.taxExemptReason}` : ""}
            {customer.taxExemptCertExpires
              ? certExpired
                ? ` · ⚠ EXPIRED ${customer.taxExemptCertExpires}`
                : ` · Expires ${customer.taxExemptCertExpires}`
              : ""}
            {certExpired && (
              <div style={{ marginTop: "4px", fontWeight: 600 }}>
                Cert is expired — exemption is invalid until refreshed,
                so sales tax IS being applied on new quotes / bills /
                invoices for this customer. Refresh the cert to restore
                exemption.
              </div>
            )}
          </div>
        );
      })()}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "8px 16px",
        }}
      >
        <div>
          <div style={dt}>Phone</div>
          <div style={dd}>{customer.phone || "—"}</div>
        </div>
        <div>
          <div style={dt}>Email</div>
          <div style={dd}>{customer.email || "—"}</div>
        </div>
        <div>
          <div style={dt}>Address</div>
          <div style={dd}>{addressLine || "—"}</div>
        </div>
        <div>
          <div style={dt}>Billing Address</div>
          <div style={dd}>{billingAddressLine || "—"}</div>
        </div>
        <div>
          <div style={dt}>Billing Contact</div>
          <div style={dd}>
            {customer.billingContactName || "—"}
            {customer.billingContactPhone
              ? ` · ${customer.billingContactPhone}`
              : ""}
            {customer.billingContactEmail
              ? ` · ${customer.billingContactEmail}`
              : ""}
          </div>
        </div>
      </div>

      {customer.notes && (
        <>
          <div style={dt}>Notes</div>
          <div style={dd}>{customer.notes}</div>
        </>
      )}

      <h4 style={{ margin: "12px 0 6px" }}>Contacts by Department</h4>
      {contacts.length === 0 ? (
        <div style={{ fontSize: "13px", color: "#666", fontStyle: "italic" }}>
          No contacts added yet. Use the Contacts button on the customer's
          detail page to add them; each contact's department drives the
          grouping here.
        </div>
      ) : (
        deptOrder.map((dept) => (
          <div key={dept} style={{ marginBottom: "6px" }}>
            <div
              style={{ fontSize: "12px", color: "#666", fontWeight: 600 }}
            >
              {dept}
            </div>
            {byDept.get(dept).map((c) => (
              <div key={c.id} style={{ fontSize: "13px" }}>
                {c.name || "(unnamed)"}
                {c.phone ? ` · ${c.phone}` : ""}
                {c.email ? ` · ${c.email}` : ""}
              </div>
            ))}
          </div>
        ))
      )}

      {onEdit && (
        <div style={{ marginTop: "10px" }}>
          <button
            style={{ ...styles.button, margin: 0 }}
            onClick={onEdit}
          >
            Edit
          </button>
        </div>
      )}
    </div>
  );
}

export default function CustomersPage({
  customers,
  contactsRaw = [],
  actions,
  role,
  capabilities,
  user,
  setSelectedCustomer,
  onBack,
}) {
  const canAddCustomers = hasCapability(role, capabilities, "addCustomers");
  const canEditCustomers = hasCapability(role, capabilities, "editCustomers");
  const canDeleteCustomers = hasCapability(
    role,
    capabilities,
    "deleteCustomers"
  );

  const [customerSearch, setCustomerSearch] = useState("");

  // Inline add / edit state.
  // mode: null | { kind: "add" } | { kind: "edit", customerId }
  const [editMode, setEditMode] = useState(null);
  const [formValue, setFormValue] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  // Which customer rows are expanded to show their info card.
  const [expanded, setExpanded] = useState({});
  // Clicking the triangle while editing THIS row auto-cancels the edit
  // (mirrors how LocationDetailPage handles it) — otherwise the user
  // gets stuck having to hit Cancel before the row will collapse.
  const toggleExpanded = (id) => {
    const closing = !!expanded[id];
    const editingThisRow =
      editMode?.kind === "edit" && editMode.customerId === id;
    if (closing && editingThisRow) {
      cancelForm();
    }
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const startAdd = () => {
    setEditMode({ kind: "add" });
    setFormValue({ ...EMPTY_FORM });
    setMsg("");
  };
  const startEdit = (c) => {
    setEditMode({ kind: "edit", customerId: c.id });
    setFormValue(buildFormFrom(c));
    setMsg("");
    // Edit form lives inside the expanded container — auto-expand so
    // clicking Edit on a collapsed row reveals the form.
    setExpanded((prev) => ({ ...prev, [c.id]: true }));
  };
  const cancelForm = () => {
    setEditMode(null);
    setFormValue(EMPTY_FORM);
    setMsg("");
  };

  const handleDelete = async () => {
    if (!editMode || editMode.kind !== "edit") return;
    try {
      setBusy(true);
      setMsg("");
      await actions.deleteCustomer(editMode.customerId);
      cancelForm();
      // Clear the expanded flag so the now-gone row doesn't leave a
      // stale open container behind.
      setExpanded((prev) => {
        const next = { ...prev };
        delete next[editMode.customerId];
        return next;
      });
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!formValue.name.trim()) {
      setMsg("Customer name is required.");
      return;
    }
    try {
      setBusy(true);
      setMsg("");
      if (editMode.kind === "edit") {
        const { ...patch } = formValue;
        await actions.updateCustomer(editMode.customerId, patch);
      } else {
        const newCustomer = {
          ...createCustomer(formValue.name.trim()),
          ...formValue,
          name: formValue.name.trim(),
        };
        await actions.addCustomer(newCustomer);
      }
      cancelForm();
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy(false);
    }
  };

  // --- Share-code linking (admin only) ---
  const [linkCode, setLinkCode] = useState("");
  const [linkMsg, setLinkMsg] = useState("");
  const [linking, setLinking] = useState(false);
  const linkBuilding = async () => {
    if (!linkCode.trim()) return;
    setLinking(true);
    setLinkMsg("");
    try {
      const fn = httpsCallable(functions, "acceptBuildingInvite");
      const res = await fn({ code: linkCode.trim() });
      setLinkMsg(res.data?.message || "Linked.");
      setLinkCode("");
    } catch (err) {
      setLinkMsg(`Couldn't link: ${err.message}`);
    } finally {
      setLinking(false);
    }
  };

  // Customer-level contacts (locationId is null/falsy means it's
  // attached to the customer itself, not a specific location).
  const customerContactsFor = (customerId) =>
    contactsRaw.filter(
      (c) => c.customerId === customerId && !c.locationId && !c.deleted
    );

  return (
    <>
      <BackButton onClick={onBack} />

      <h2>Customers</h2>

      {canAddCustomers && editMode?.kind !== "add" && (
        <button
          style={{ ...styles.primaryButton, margin: "0 0 12px" }}
          onClick={startAdd}
        >
          + Add Customer
        </button>
      )}
      {editMode?.kind === "add" && (
        <CustomerForm
          mode="add"
          value={formValue}
          onChange={setFormValue}
          onSave={handleSave}
          onCancel={cancelForm}
          busy={busy}
        />
      )}
      {msg && (
        <div style={{ color: "red", fontSize: "12px", margin: "4px 0" }}>
          {msg}
        </div>
      )}

      <input
        style={{ ...styles.input, marginTop: "6px" }}
        placeholder="Search customers..."
        value={customerSearch}
        onChange={(e) => setCustomerSearch(e.target.value)}
      />
      <button
        style={{ ...styles.button, marginLeft: "4px" }}
        onClick={() => setCustomerSearch("")}
      >
        Clear
      </button>

      <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0" }}>
        {[...customers]
          .filter((customer) =>
            customer.name.toLowerCase().includes(customerSearch.toLowerCase())
          )
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((customer) => {
            const isExpanded = !!expanded[customer.id];
            const isEditingThis =
              editMode?.kind === "edit" && editMode.customerId === customer.id;
            return (
              <li key={customer.id} style={{ ...styles.card, maxWidth: "780px" }}>
                <div
                  onClick={() => toggleExpanded(customer.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    cursor: "pointer",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "monospace",
                      color: "#666",
                      width: "12px",
                    }}
                  >
                    {isExpanded ? "▼" : "▶"}
                  </span>
                  <strong>
                    <HighlightText
                      text={customer.name}
                      search={customerSearch}
                    />
                  </strong>
                  <span style={{ fontSize: "12px", color: "#666" }}>
                    Locations: {customer.locations.length}
                  </span>
                  <div
                    style={{
                      marginLeft: "auto",
                      display: "flex",
                      gap: "6px",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      style={{ ...styles.button, margin: 0 }}
                      onClick={() => setSelectedCustomer(customer)}
                    >
                      Open
                    </button>
                  </div>
                </div>

                {isExpanded &&
                  (isEditingThis ? (
                    <div style={{ marginTop: "8px" }}>
                      <CustomerForm
                        mode="edit"
                        value={formValue}
                        onChange={setFormValue}
                        onSave={handleSave}
                        onCancel={cancelForm}
                        onDelete={handleDelete}
                        canDelete={canDeleteCustomers}
                        busy={busy}
                        customerId={customer.id}
                        contacts={customerContactsFor(customer.id)}
                        actions={actions}
                        currentUserEmail={user?.email}
                      />
                    </div>
                  ) : (
                    <CustomerInfoCard
                      customer={customer}
                      contacts={customerContactsFor(customer.id)}
                      onEdit={canEditCustomers ? () => startEdit(customer) : null}
                    />
                  ))}
              </li>
            );
          })}
      </ul>

      {isAdminTier(role) && (
        <div style={{ ...styles.card, maxWidth: "780px", marginTop: "16px" }}>
          <strong>Link by share code</strong>
          <p style={{ fontSize: "12px", color: "#666", margin: "4px 0" }}>
            Enter a share code to link a single building, or a whole customer
            (all its buildings) — works for either kind of code.
          </p>
          <input
            style={styles.input}
            placeholder="Share code"
            value={linkCode}
            onChange={(e) => setLinkCode(e.target.value)}
          />
          <button
            style={styles.button}
            onClick={linkBuilding}
            disabled={linking || !linkCode.trim()}
          >
            {linking ? "Linking..." : "Link Building"}
          </button>
          {linkMsg && (
            <p style={{ fontSize: "12px", marginTop: "4px" }}>{linkMsg}</p>
          )}
        </div>
      )}
    </>
  );
}
